import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  Card,
  CardContent,
} from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Heart, Share2, MoreHorizontal, Trash2, MessageCircle } from 'lucide-react';
import PostModal from './PostModal';
import HashtagText from './ui/HashtagText';

// Back arrow icon component
const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

// Memoized UserCard component for performance
const UserCard = memo<{ user: User }>(({ user }) => (
  <Card className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900 will-change-transform">
    <CardContent className="p-4">
      <Link to={`/@${user.handle}`} className="block hover:opacity-80 transition-opacity">
        <div className="flex items-start space-x-3">
          <Avatar className="w-12 h-12 flex-shrink-0">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-parchment-200 text-custom-blue-800">
              {user.name.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-custom-blue-900 truncate">{user.name}</h3>
            <p className="text-sm text-custom-blue-600">@{user.handle}</p>
            {user.headline && (
              <p className="text-sm text-custom-blue-700 mt-1 line-clamp-2">{user.headline}</p>
            )}
          </div>
        </div>
      </Link>
    </CardContent>
  </Card>
));

UserCard.displayName = 'UserCard';

// Memoized PostItem component for performance
const PostItem = memo<{
  post: Post;
  currentUserHandle?: string;
  isAuthenticated: boolean;
  onPostClick: (post: Post) => void;
  onLikePost: (postId: string) => void;
  onSharePost: (post: Post, event: React.MouseEvent) => void;
  onPostDelete: (postId: string) => void;
  formatDate: (dateString: string) => string;
}>(({ post, currentUserHandle, isAuthenticated, onPostClick, onLikePost, onSharePost, onPostDelete, formatDate }) => (
  <Card 
    key={post._id} 
    className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900 will-change-transform"
  >
    <CardContent className="p-4 pt-2 pb-1">
      <div 
        className="space-y-3 cursor-pointer hover:opacity-95 transition-opacity"
        onClick={() => onPostClick(post)}
      >
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link 
            to={`/@${post.user.handle}`}
            className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Avatar className="w-12 h-12">
              <AvatarImage src={post.user.picture} alt={post.user.name} />
              <AvatarFallback className="bg-parchment-200 text-custom-blue-800">
                {post.user.name.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </Link>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <Link 
                to={`/@${post.user.handle}`}
                className="cursor-pointer hover:text-custom-blue-700 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="font-semibold text-custom-blue-900 text-sm">
                  {post.user.name}
                </span>
              </Link>
              <Link 
                to={`/@${post.user.handle}`}
                className="cursor-pointer hover:text-custom-blue-500 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-sm text-custom-blue-600">
                  @{post.user.handle}
                </span>
              </Link>
              <span className="text-sm text-custom-blue-500">•</span>
              <span className="text-sm text-custom-blue-500">
                {formatDate(post.createdAt)}
              </span>
            </div>
          </div>
          
          <div className="flex-shrink-0">
            {currentUserHandle === post.user.handle && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="bg-paper border-parchment-300 z-[9999] shadow-lg"
                  sideOffset={5}
                >
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onPostDelete(post._id);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete this post
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Post Content */}
        <div className="ml-12 pr-4">
          <HashtagText 
            text={post.content}
            className="text-sm text-custom-blue-800 leading-relaxed whitespace-pre-wrap break-words text-left block"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between ml-12 pr-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onPostClick(post);
            }}
            className="h-8 p-2 text-sm text-custom-blue-700 hover:bg-parchment-100 flex items-center space-x-2"
          >
            <MessageCircle className="w-4 h-4" />
            <span>{post.commentCount} {post.commentCount === 1 ? 'comment' : 'comments'}</span>
          </Button>

          <div className="flex items-center space-x-2">
            {isAuthenticated ? (
              // Show interactive buttons for authenticated users
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLikePost(post._id);
                  }}
                  className={`h-10 w-10 p-0 hover:bg-parchment-100 transition-colors ${post.isLiked ? 'text-red-500' : 'text-custom-blue-600'}`}
                >
                  <Heart 
                    className="w-5 h-5" 
                    fill={post.isLiked ? 'currentColor' : 'none'} 
                  />
                </Button>
                {post.likeCount > 0 && (
                  <span className="text-sm text-gray-600 ml-1">
                    {post.likeCount}
                  </span>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100 transition-colors"
                  onClick={(e) => onSharePost(post, e)}
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </>
            ) : (
              // Show static view for non-authenticated users
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1 text-custom-blue-600">
                  <Heart className="w-5 h-5" fill="none" />
                  {post.likeCount > 0 && (
                    <span className="text-sm text-gray-600">
                      {post.likeCount}
                    </span>
                  )}
                </div>
                <div className="text-custom-blue-600">
                  <Share2 className="w-5 h-5" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
));

PostItem.displayName = 'PostItem';

interface User {
  _id: string;
  name: string;
  handle: string;
  picture: string;
  headline?: string;
  bio?: string;
}

interface Post {
  _id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  isLiked: boolean;
  commentCount: number;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  deleted?: boolean;
}

interface SearchResults {
  users: User[];
  posts: Post[];
  pagination: {
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
    userCount: number;
    postCount: number;
  };
}

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const query = searchParams.get('q') || '';
  const tab = searchParams.get('tab') || 'all';

  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalPost, setModalPost] = useState<Post | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const [deletedPostIds, setDeletedPostIds] = useState<Set<string>>(new Set());

  // Perform search
  const performSearch = useCallback(async (searchQuery: string, searchTab: string, page: number = 1) => {
    if (!searchQuery.trim()) {
      setResults(null);
      return;
    }

    setLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&type=${searchTab}&page=${page}&limit=20`,
        {
          method: 'GET',
          headers: headers,
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        if (page === 1) {
          setResults(data.data);
        } else {
          // Append results for pagination
          setResults(prev => {
            if (!prev) return data.data;
            
            return {
              ...data.data,
              // For 'all' searches, only append users on subsequent pages if we're in 'users' tab
              users: searchTab === 'users' ? [...prev.users, ...data.data.users] : 
                     searchTab === 'all' ? prev.users : // Keep existing users for 'all', don't append
                     data.data.users,
              // Always append posts for pagination in 'posts' and 'all' tabs
              posts: (searchTab === 'posts' || searchTab === 'all') ? [...prev.posts, ...data.data.posts] : data.data.posts,
            };
          });
        }
      } else {
        console.error('Search failed:', data.message);
        setResults(null);
      }
    } catch (error) {
      console.error('Error searching:', error);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search when query or tab changes
  useEffect(() => {
    if (query) {
      setCurrentPage(1);
      // Clear deleted posts when performing a new search
      setDeletedPostIds(new Set());
      
      // Auto-switch to posts tab for hashtag searches
      const isHashtagSearch = query.trim().startsWith('#');
      const effectiveTab = isHashtagSearch ? 'posts' : tab;
      
      performSearch(query, effectiveTab, 1);
    }
  }, [query, tab, performSearch]);

  // Handle post updates within modal  
  const handlePostUpdate = (updatedPost: Post) => {
    // Update the post in results if it exists there
    setResults(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        posts: prev.posts.map(post => 
          post._id === updatedPost._id ? updatedPost : post
        )
      };
    });
    
    // Update modal post if it's the same post
    if (modalPost && modalPost._id === updatedPost._id) {
      setModalPost(updatedPost);
    }
  };

  // Handle post deletion from dropdown
  const handlePostDelete = (postId: string) => {
    if (!user) {
      addToast('Please log in to delete posts', 'error');
      return;
    }

    // Open confirmation dialog
    setPostToDelete(postId);
    setDeleteDialogOpen(true);
  };

  // Actual deletion after confirmation
  const confirmDeletePost = async () => {
    if (!postToDelete) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/posts/${postToDelete}`,
        {
          method: 'DELETE',
          headers: headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete post');
      }

      // Track that this post has been deleted
      setDeletedPostIds(prev => new Set(prev).add(postToDelete));

      // Remove from results
      setResults(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          posts: prev.posts.filter(post => post._id !== postToDelete)
        };
      });

      // Close dialog
      setDeleteDialogOpen(false);
      setPostToDelete(null);

      addToast('Post deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting post:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to delete post', 
        'error'
      );
    }
  };

  // Close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  // Handle post deletion from modal
  const handlePostDeleteFromModal = (postId: string) => {
    // Track that this post has been deleted
    setDeletedPostIds(prev => new Set(prev).add(postId));
    
    // Remove from results
    setResults(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        posts: prev.posts.filter(post => post._id !== postId)
      };
    });
    
    // Close modal - inline to avoid dependency issues
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('modal');
    navigate(`/search?${newSearchParams.toString()}`);
    // Note: Toast is handled by PostModal, so we don't show one here
  };

  // Handle closing modal
  const handleCloseModal = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('modal');
    navigate(`/search?${newSearchParams.toString()}`);
  };

  // Handle modal opening when modalPostId changes
  useEffect(() => {
    const modalPostId = searchParams.get('modal');
    if (modalPostId) {
      // Check if this post has been deleted
      if (deletedPostIds.has(modalPostId)) {
        // Post was deleted, close modal
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('modal');
        navigate(`/search?${newSearchParams.toString()}`);
        return;
      }

      // Check if post exists in current results
      const existingPost = results?.posts.find(p => p._id === modalPostId);
      if (existingPost) {
        setModalPost(existingPost);
      } else {
        // Fetch the post separately
        const fetchPostById = async (postId: string) => {
          try {
            const sessionToken = localStorage.getItem('session_token');
            const headers: { [key: string]: string } = {
              'Content-Type': 'application/json',
            };

            if (sessionToken) {
              headers['Authorization'] = `Bearer ${sessionToken}`;
            }

            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/posts/${postId}`,
              {
                method: 'GET',
                headers: headers,
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.status === 'success') {
                setModalPost(data.data);
              } else {
                console.error('Failed to fetch post:', data.message);
                // Close modal inline
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('modal');
                navigate(`/search?${newSearchParams.toString()}`);
              }
            } else {
              console.error('Failed to fetch post, status:', response.status);
              // Close modal inline
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('modal');
              navigate(`/search?${newSearchParams.toString()}`);
            }
          } catch (error) {
            console.error('Error fetching post:', error);
            // Close modal inline
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('modal');
            navigate(`/search?${newSearchParams.toString()}`);
          }
        };
        
        fetchPostById(modalPostId);
      }
    } else {
      setModalPost(null);
    }
  }, [searchParams, results, navigate, deletedPostIds]);

  // Handle tab change - memoized for performance
  const handleTabChange = useCallback((newTab: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', newTab);
    navigate(`/search?${newSearchParams.toString()}`);
  }, [searchParams, navigate]);

  // Handle load more - memoized for performance
  const handleLoadMore = useCallback(() => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    performSearch(query, tab, nextPage);
  }, [currentPage, performSearch, query, tab]);

  // Handle like post - memoized for performance
  const handleLikePost = useCallback(async (postId: string) => {
    if (!user) {
      addToast('Please log in to like posts', 'error');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const currentPost = results?.posts.find(p => p._id === postId);
      if (!currentPost) return;

      const isCurrentlyLiked = currentPost.isLiked;
      const url = `${process.env.REACT_APP_API_URL}/api/posts/${postId}/like`;
      const method = isCurrentlyLiked ? 'DELETE' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update like status');
      }

      const result = await response.json();

      // Update the posts in results
      setResults(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          posts: prev.posts.map(post => 
            post._id === postId 
              ? {
                  ...post,
                  likeCount: result.data.likeCount,
                  isLiked: result.data.isLiked
                }
              : post
          )
        };
      });

    } catch (error) {
      console.error('Error updating like status:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to update like status', 
        'error'
      );
    }
  }, [user, addToast, results]);

  // Handle share post - memoized for performance
  const handleSharePost = useCallback(async (post: Post, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const postUrl = `${window.location.origin}/@${post.user.handle}/post/${post._id}`;
    
    try {
      await navigator.clipboard.writeText(postUrl);
      addToast('Post link copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      addToast('Unable to copy automatically. Please copy manually.', 'error');
    }
  }, [addToast]);

  // Handle post click - keep the user on the search page with modal - memoized for performance
  const handlePostClick = useCallback((post: Post) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('modal', post._id);
    navigate(`/search?${newSearchParams.toString()}`);
  }, [searchParams, navigate]);

  // Format date function - memoized for performance
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }, []);

  if (!query) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen py-2">
        <main className="flex flex-col items-center justify-center w-full flex-1 px-4 md:px-20 text-center">
          <Card className="bg-paper border-parchment-300 text-custom-blue-900 shadow-paper max-w-md">
            <CardContent className="text-center py-12">
              <p className="text-lg mb-4">🔍 Search The Myth Dimension</p>
              <p className="text-custom-blue-700">Enter a search term to find users and posts.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .search-page-performance {
          contain: layout style paint;
        }
        .search-results-performance {
          contain: layout style paint;
        }
      `}</style>
      <div className="flex flex-col items-center py-2 will-change-transform search-page-performance">
        <main className="flex flex-col items-center w-full px-4 md:px-20">
          <div className="w-full max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <Button
                onClick={() => navigate('/feed')}
                className="flex items-center space-x-2 bg-custom-blue-600 hover:bg-custom-blue-700 text-white transition-colors mr-6 px-3 py-2 rounded-md border border-custom-blue-600"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="font-medium">Back to Feed</span>
              </Button>
            </div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-white mb-2">
                Search Results
              </h1>
              <p className="text-lg text-gray-300">
                Showing results for "<span className="font-semibold text-white">{query}</span>"
              </p>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-parchment-100 p-1 rounded-lg border border-parchment-200">
              {!query.trim().startsWith('#') && (
                <Button
                  variant={tab === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleTabChange('all')}
                  className={tab === 'all' 
                    ? 'bg-custom-blue-600 text-white shadow-sm font-medium' 
                    : 'text-custom-blue-700 hover:bg-parchment-200 font-medium'
                  }
                >
                  All Results ({(results?.pagination.userCount || 0) + (results?.pagination.postCount || 0)})
                </Button>
              )}
              {!query.trim().startsWith('#') && (
                <Button
                  variant={tab === 'users' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleTabChange('users')}
                  className={tab === 'users' 
                    ? 'bg-custom-blue-600 text-white shadow-sm font-medium' 
                    : 'text-custom-blue-700 hover:bg-parchment-200 font-medium'
                  }
                >
                  Users ({results?.pagination.userCount || 0})
                </Button>
              )}
              <Button
                variant={(tab === 'posts' || query.trim().startsWith('#')) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTabChange('posts')}
                className={(tab === 'posts' || query.trim().startsWith('#'))
                  ? 'bg-custom-blue-600 text-white shadow-sm font-medium' 
                  : 'text-custom-blue-700 hover:bg-parchment-200 font-medium'
                }
              >
                Posts ({results?.pagination.postCount || 0})
                {query.trim().startsWith('#') && <span className="ml-1 text-xs opacity-80">(hashtag)</span>}
              </Button>
            </div>
          </div>

          {/* Results */}
          {loading && !results && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-blue-600 mx-auto"></div>
              <p className="mt-2 text-custom-blue-700">Searching...</p>
            </div>
          )}

          {results && (
            <div className="space-y-6 will-change-transform search-results-performance">
              {/* Users Section */}
              {(tab === 'all' || tab === 'users') && results.users.length > 0 && (
                <div>
                  {tab === 'all' && (
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <span className="bg-custom-blue-100 text-custom-blue-800 px-3 py-1 rounded-full text-sm font-semibold mr-3">
                        {results.users.length}
                      </span>
                      Users Found
                    </h2>
                  )}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {results.users.map((searchUser) => (
                      <UserCard key={searchUser._id} user={searchUser} />
                    ))}
                  </div>
                </div>
              )}

              {/* Posts Section */}
              {(tab === 'all' || tab === 'posts') && results.posts.length > 0 && (
                <div>
                  {tab === 'all' && (
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <span className="bg-custom-blue-100 text-custom-blue-800 px-3 py-1 rounded-full text-sm font-semibold mr-3">
                        {results.posts.length}
                      </span>
                      Posts Found
                    </h2>
                  )}
                  <div className="space-y-4">
                    {results.posts.map((post) => (
                      <PostItem
                        key={post._id}
                        post={post}
                        currentUserHandle={user?.handle}
                        isAuthenticated={!!user}
                        onPostClick={handlePostClick}
                        onLikePost={handleLikePost}
                        onSharePost={handleSharePost}
                        onPostDelete={handlePostDelete}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Load More Button */}
              {results.pagination.hasNextPage && (
                <div className="text-center py-4">
                  <Button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}

              {/* No Results */}
              {results.users.length === 0 && results.posts.length === 0 && !loading && (
                <Card className="bg-paper border-parchment-300 text-custom-blue-900 shadow-paper">
                  <CardContent className="text-center py-16">
                    <div className="text-6xl mb-6">🔍</div>
                    <h3 className="text-xl font-bold text-custom-blue-900 mb-3">No Results Found</h3>
                    <p className="text-custom-blue-700 text-lg mb-4">
                      We couldn't find anything matching "<span className="font-semibold">{query}</span>"
                    </p>
                    <p className="text-custom-blue-600">
                      Try searching with different keywords or check your spelling.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Post Modal */}
      {modalPost && (
        <PostModal
          post={modalPost}
          isOpen={!!modalPost}
          onClose={handleCloseModal}
          onPostUpdate={handlePostUpdate}
          onPostDelete={handlePostDeleteFromModal}
        />
      )}

      {/* Delete Post Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-paper border-parchment-300 text-custom-blue-900">
          <DialogHeader>
            <DialogTitle className="text-custom-blue-900">Delete Post</DialogTitle>
            <DialogDescription className="text-custom-blue-700">
              Are you sure you want to delete this post? This action cannot be undone and will also delete all comments on this post.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={closeDeleteDialog}
              className="border-parchment-300 text-custom-blue-700 hover:bg-parchment-100"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeletePost}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default SearchPage;
